// summary.jsx — post-workout summary: the payoff screen.
//
// Stats count up, PRs show before → after, the muscle map shows what was
// trained, progression tells you next time's targets, and a debrief explains
// the session (Claude when connected, an on-device summary otherwise). Share
// renders a real image (canvas) and hands it to the OS share sheet.

import React from 'react';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { MuscleMap } from './musclemap.jsx';
import { BackButton } from './phone.jsx';
import { LLSheet } from './sheets.jsx';
import { WeightRepInput } from './workout.jsx';
import { slotId, sessionSlots, slotFromExercise } from './session_model.js';
import {
  e1rm, musclesFor, indexExercises, kindOf, setLoad, isStrengthSet, hardSetValue, setsVolume, paceSecPerKm, fmtPace,
  fmtDistance, distUnit, fmtDuration, fmtHold, setText, effortText, rirFromRpe, parseDuration, metersFrom, metersTo,
  muscleFeedbackTrend, isBwLoad,
} from './analytics.js';
import { MuscleCheck, FinishCheckIn } from './active.jsx';
import { ExercisePicker } from './exercise_picker.jsx';
import { AchievementsUnlocked } from './achievements_ui.jsx';
import { exerciseNameFor, localDateStr } from './utils.jsx';
import { aiEnabled, completeText, openCoachWith, useAIStatus } from './ai.js';
import { coachContext } from './coach_tools.js';
import { simpleDefault } from './glossary.js';

const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.1, color: LL.fgDim, textTransform: 'uppercase' };
const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function fmtDur(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (!h && m < 1) return '<1 min';
  return h ? `${h}h ${m}m` : `${m} min`;
}
const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const volStr = (lb, units) => { const v = units === 'kg' ? lb * 0.45359237 : lb; return v >= 10000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString(); };

function useCountUp(target, ms = 700) {
  const [v, setV] = React.useState(reduce ? target : 0);
  React.useEffect(() => {
    if (reduce) { setV(target); return; }
    let raf, start;
    const step = (t) => { if (!start) start = t; const p = Math.min(1, (t - start) / ms); setV(target * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

// Everything the summary needs, derived from the session + prior history.
// Loads are EFFECTIVE (body weight ± added / assistance for bodyweight lifts);
// timed holds and cardio get their own records (longest hold, fastest pace,
// longest distance), never a fake e1RM.
export function summarize(state, session) {
  const idx = indexExercises(state);
  const prior = state.sessions.filter(s => s.completedAt && s.id !== session.id && s.startedAt < session.startedAt);
  const work = session.sets.filter(s => s.type !== 'warmup' && (s.reps > 0 || s.dist > 0 || s.dur > 0));
  const volume = setsVolume(state, session.sets, session.startedAt);
  const slots = sessionSlots(session, state.programs);
  const kindCache = {};
  const kindFor = (id) => kindCache[id] || (kindCache[id] = kindOf(state, id));
  const strengthE1 = (st, at) => {
    const k = kindFor(st.exerciseId);
    if (k.track !== 'reps' || !isStrengthSet(st)) return 0;
    const eff = setLoad(state, st, k, at);
    return eff > 0 ? e1rm(eff, st.reps) : 0;
  };
  const hardSets = work.reduce((a, s) => a + (kindFor(s.exerciseId).track === 'distance-time' ? 0 : hardSetValue(s)), 0);

  // PRs: best e1RM this session vs best before it (strength), and for holds /
  // cardio the longest hold, fastest pace and longest distance.
  const priorBest = {}, priorHold = {}, priorPace = {}, priorDist = {};
  const seenBefore = new Set();
  for (const s of prior) for (const st of s.sets || []) {
    if (st.type === 'warmup') continue;
    seenBefore.add(st.exerciseId);
    const k = kindFor(st.exerciseId);
    if (k.track === 'reps') {
      const v = strengthE1(st, s.startedAt);
      if (v > 0 && (!priorBest[st.exerciseId] || v > priorBest[st.exerciseId].e1rm)) priorBest[st.exerciseId] = { e1rm: v, st };
    } else if (k.track === 'time') {
      if (st.reps > 0 && (!priorHold[st.exerciseId] || st.reps > priorHold[st.exerciseId].st.reps)) priorHold[st.exerciseId] = { st };
    } else {
      const p = st.dist >= 400 ? paceSecPerKm(st.dist, st.dur) : null;
      if (p && (!priorPace[st.exerciseId] || p < priorPace[st.exerciseId].pace)) priorPace[st.exerciseId] = { pace: p, st };
      if (st.dist > 0 && (!priorDist[st.exerciseId] || st.dist > priorDist[st.exerciseId].st.dist)) priorDist[st.exerciseId] = { st };
    }
  }
  const bestNow = {}, holdNow = {}, paceNow = {}, distNow = {};
  for (const st of work) {
    const k = kindFor(st.exerciseId);
    if (k.track === 'reps') {
      const v = strengthE1(st, session.startedAt);
      if (v > 0 && (!bestNow[st.exerciseId] || v > bestNow[st.exerciseId].e1rm)) bestNow[st.exerciseId] = { e1rm: v, st };
    } else if (k.track === 'time') {
      if (st.reps > 0 && (!holdNow[st.exerciseId] || st.reps > holdNow[st.exerciseId].st.reps)) holdNow[st.exerciseId] = { st };
    } else {
      const p = st.dist >= 400 ? paceSecPerKm(st.dist, st.dur) : null;
      if (p && (!paceNow[st.exerciseId] || p < paceNow[st.exerciseId].pace)) paceNow[st.exerciseId] = { pace: p, st };
      if (st.dist > 0 && (!distNow[st.exerciseId] || st.dist > distNow[st.exerciseId].st.dist)) distNow[st.exerciseId] = { st };
    }
  }
  const name = (id) => exerciseNameFor(state, id);
  const prs = [
    ...Object.entries(bestNow).filter(([id, b]) => priorBest[id] && b.e1rm > priorBest[id].e1rm + 0.01)
      .map(([id, b]) => ({ exerciseId: id, metric: 'e1rm', name: name(id), now: b, before: priorBest[id], gainPct: (b.e1rm / priorBest[id].e1rm - 1) * 100 })),
    ...Object.entries(holdNow).filter(([id, b]) => priorHold[id] && b.st.reps > priorHold[id].st.reps)
      .map(([id, b]) => ({ exerciseId: id, metric: 'hold', name: name(id), now: b, before: priorHold[id], gainPct: (b.st.reps / priorHold[id].st.reps - 1) * 100 })),
    // Cardio: one record per exercise: the pace PR, also naming a distance PR.
    ...[...new Set([...Object.keys(paceNow), ...Object.keys(distNow)])].map(id => {
      const pace = paceNow[id] && priorPace[id] && paceNow[id].pace < priorPace[id].pace - 0.5
        ? { exerciseId: id, metric: 'pace', name: name(id), now: paceNow[id], before: priorPace[id], gainPct: (priorPace[id].pace / paceNow[id].pace - 1) * 100 } : null;
      const dist = distNow[id] && priorDist[id] && distNow[id].st.dist > priorDist[id].st.dist + 1
        ? { exerciseId: id, metric: 'distance', name: name(id), now: distNow[id], before: priorDist[id], gainPct: (distNow[id].st.dist / priorDist[id].st.dist - 1) * 100 } : null;
      if (pace && dist) return { ...pace, also: 'longest distance' };
      return pace || dist;
    }).filter(Boolean),
  ].sort((a, b) => b.gainPct - a.gainPct);

  // Lifts never done before: today's best is the first baseline.
  const firsts = [...new Set(work.map(s => s.exerciseId))].filter(id => !seenBefore.has(id)).length;

  // Muscles (hard sets: mini-sets count by technique, cardio not at all)
  const muscles = {};
  for (const st of work) {
    if (kindFor(st.exerciseId).track === 'distance-time') continue;
    const v = hardSetValue(st);
    if (!v) continue;
    const { primary, secondary } = musclesFor(idx[st.exerciseId]);
    primary.forEach(m => { muscles[m] = (muscles[m] || 0) + v; });
    secondary.forEach(m => { muscles[m] = (muscles[m] || 0) + v * 0.5; });
  }
  const maxM = Math.max(1, ...Object.values(muscles));
  const muscleValues = Object.fromEntries(Object.entries(muscles).map(([k, v]) => [k, v / maxM]));

  // Compare with the last time this same day (or same exercises) was done
  const lastSame = [...prior].reverse().find(s => (session.dayId && s.dayId === session.dayId));
  const lastVol = lastSame ? setsVolume(state, lastSame.sets, lastSame.startedAt) : null;

  // A planned easy day (deload week, "lighter" check-in) is lighter by design:
  // never judge it against the last heavy session.
  const easyDay = !!(session.deload || session.planDeload || session.lighter);

  // Per-exercise recap. Scheme-driven lifts (waves, %TM, top sets, stages) are
  // judged against what was prescribed for these rows, not against last time:
  // a 50% BBB set or a deload week is not a regression.
  const exercises = slots.map(sl => {
    const sets = session.sets.filter(s => s.peId === sl.id).sort((a, b) => a.setIdx - b.setIdx);
    if (!sets.length) return null;
    const kind = kindFor(sl.exerciseId);
    const k = sl.unit === 'sec' ? { ...kind, track: 'time' } : sl.track === 'distance-time' ? { ...kind, track: 'distance-time' } : kind;
    const scored = sets.map(s => ({ s, v: k.track === 'reps' ? strengthE1(s, session.startedAt) : k.track === 'time' ? (s.reps || 0) : (s.dist || s.dur || 0) }));
    const top = scored.filter(x => x.s.type !== 'warmup').reduce((b, x) => (!b || x.v > b.v ? x : b), null);
    const best = top && top.v > 0 ? top.s : null;
    const planned = !!sl.scheme && sl.scheme !== 'double' && (sl.rows || []).some(r => r.reps > 0);
    let plan = null;
    if (planned) {
      let hit = 0, short = 0, plus = null;
      (sl.rows || []).forEach((r, i) => {
        if (r.type === 'warmup' || r.type === 'drop' || r.type === 'mini' || !(r.reps > 0)) return;
        const st = sets.find(x => x.setIdx === i);
        if (!st) return;
        const made = (st.reps || 0) >= r.reps && (!(r.w > 0) || (st.w || 0) >= r.w * 0.97);
        if (made) hit++; else short++;
        if (r.amrap) plus = { reps: st.reps || 0, min: r.reps, target: r.target || r.reps };
      });
      plan = { hit, short, plus, total: (sl.rows || []).filter(r => r.type !== 'warmup' && r.type !== 'drop' && r.type !== 'mini' && r.reps > 0).length };
    }
    let delta = null;
    if (!planned && !easyDay && best && k.track === 'reps') {
      const lastTime = [...prior].reverse().map(s => ({ s, sets: (s.sets || []).filter(x => x.exerciseId === sl.exerciseId && x.type !== 'warmup') })).find(a => a.sets.length);
      const lastBest = lastTime ? Math.max(0, ...lastTime.sets.map(x => strengthE1(x, lastTime.s.startedAt))) : 0;
      const nowBest = strengthE1(best, session.startedAt);
      if (lastBest > 0 && nowBest > 0) delta = (nowBest / lastBest - 1) * 100;
    }
    return { slot: sl, name: sets.find(s => s.performedName)?.performedName || sl.name, sets, best, delta, timed: k.track === 'time', kind: k, plan };
  }).filter(Boolean);
  const planned = exercises.some(e => e.plan);

  return {
    duration: Math.max(0, Math.floor(((session.completedAt || Date.now()) - session.startedAt) / 1000)),
    volume, sets: hardSets, prs, firsts, muscleValues, muscles, exercises, easyDay, planned,
    // Volume vs last time only means something when both days asked for the same work.
    lastVol: easyDay || (planned && lastSame && lastSame.weekIndex !== session.weekIndex) ? null : lastVol,
  };
}

// "W ", "D ", "M ", "+ " … before a set chip.
const TYPE_PREFIX = { warmup: 'W ', drop: 'D ', failure: 'F ', myo: 'M ', restpause: 'RP ', cluster: 'C ', mini: '+ ' };
// PR line: "185 × 8 → 195 × 8 · e1RM 225 lb", "0:45 → 1:05", "5:30 → 5:06 /km".
function prLine(p, units) {
  if (p.metric === 'hold') return { from: fmtHold(p.before.st.reps), to: fmtHold(p.now.st.reps), tail: 'longest hold' };
  if (p.metric === 'pace') return { from: fmtPace(p.before.pace, units), to: fmtPace(p.now.pace, units), tail: p.also ? `fastest pace · ${p.also} too` : 'fastest pace' };
  if (p.metric === 'distance') return { from: `${fmtDistance(p.before.st.dist, units)} ${distUnit(units)}`, to: `${fmtDistance(p.now.st.dist, units)} ${distUnit(units)}`, tail: 'longest distance' };
  return { from: null, to: null, tail: null };
}

function localDebrief(sum, units, session, simple = false) {
  const parts = [];
  if (session.lighter) parts.push('Lighter session, as you chose: one less set per lift and a little less weight. Showing up on a rough day keeps the habit alive.');
  else if (session.deload || session.planDeload) parts.push('Deload week, lighter by design. This is where the last few weeks of work turn into strength, so loads and volume are meant to be down.');
  const plans = sum.exercises.filter(e => e.plan);
  const beats = plans.filter(e => e.plan.plus && e.plan.plus.reps > e.plan.plus.min)
    .sort((a, b) => (b.plan.plus.reps - b.plan.plus.min) - (a.plan.plus.reps - a.plan.plus.min));
  if (beats.length) {
    const b = beats[0];
    parts.push(`${b.plan.plus.reps} rep${b.plan.plus.reps === 1 ? '' : 's'} on the ${b.name} + set, ${b.plan.plus.reps - b.plan.plus.min} past the minimum${beats.length > 1 ? `, and ${beats.length - 1} more + set${beats.length > 2 ? 's' : ''} beaten` : ''}.`);
  }
  const allMade = plans.filter(e => e.plan.short === 0 && e.plan.hit > 0);
  if (plans.length && allMade.length === plans.length && !sum.easyDay) parts.push(`Every prescribed set made${plans.length > 1 ? ` across ${plans.length} lifts` : ''}.`);
  const missed = plans.filter(e => e.plan.short > 0);
  const nMissed = missed.reduce((a, e) => a + e.plan.short, 0);
  if (missed.length && !sum.easyDay) parts.push(`${missed.map(e => e.name).slice(0, 2).join(' and ')} came up short of the plan on ${plural(nMissed, 'set')}.`);
  if (sum.lastVol) {
    const p = Math.round((sum.volume / sum.lastVol - 1) * 100);
    parts.push(p >= 0 ? `Volume up ${p}% on last ${session.name || 'session'}.` : `Volume ${Math.abs(p)}% below last ${session.name || 'session'} — fine if the weights went up.`);
  }
  if (sum.prs.length) {
    const p0 = sum.prs[0];
    const what = p0.metric === 'pace' ? 'faster pace' : p0.metric === 'distance' ? 'further' : p0.metric === 'hold' ? 'longer hold' : simple ? 'estimated max' : 'e1RM';
    parts.push(`${sum.prs.length} new PR${sum.prs.length > 1 ? 's' : ''}, led by ${p0.name} (+${p0.gainPct.toFixed(1)}% ${what}).`);
  }
  const cardio = sum.exercises.filter(e => e.kind?.track === 'distance-time');
  if (cardio.length) {
    const d = cardio.reduce((a, e) => a + e.sets.reduce((x, st) => x + (st.dist || 0), 0), 0);
    const t = cardio.reduce((a, e) => a + e.sets.reduce((x, st) => x + (st.dur || 0), 0), 0);
    if (d > 0 || t > 0) parts.push(`Cardio: ${[d > 0 ? `${fmtDistance(d, units)} ${distUnit(units)}` : null, t > 0 ? fmtDuration(t) : null].filter(Boolean).join(' in ')}${d >= 400 && t > 0 ? ` (${fmtPace(paceSecPerKm(d, t), units)})` : ''}.`);
  }
  const hurt = Object.entries(session.feedback || {}).filter(([, f]) => f && f.joint === 'bad').map(([m]) => m.toLowerCase());
  if (hurt.length) parts.push(`Joint pain on ${hurt.join(' and ')}: those lifts will offer a swap next time.`);
  // delta is null for planned lifts and easy days, so these only judge free lifts.
  const up = sum.exercises.filter(e => e.delta != null && e.delta > 0.5).length;
  const down = sum.easyDay ? 0 : sum.exercises.filter(e => e.delta != null && e.delta < -2).length;
  if (up) parts.push(`${up} lift${up > 1 ? 's' : ''} stronger than last time.`);
  if (down) parts.push(`${down} lift${down > 1 ? 's' : ''} dipped — check sleep and food before adding load.`);
  const top = Object.entries(sum.muscles).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([m]) => m.toLowerCase());
  if (top.length) parts.push(`Most work went to ${top.join(' and ')}.`);
  return parts.join(' ') || 'Session logged. Consistency is the whole game.';
}

function Confetti() {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (reduce) return;
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.width = c.offsetWidth * 2, H = c.height = c.offsetHeight * 2;
    const css = getComputedStyle(document.documentElement);
    const accent = css.getPropertyValue('--ll-accent').trim() || 'oklch(0.86 0.19 132)';
    const colors = [accent, 'oklch(0.82 0.18 85)', '#ffffff', 'oklch(0.72 0.20 245)'];
    const parts = Array.from({ length: 110 }, () => ({
      x: W / 2 + (Math.random() - 0.5) * W * 0.2, y: H * 0.32,
      vx: (Math.random() - 0.5) * 26, vy: -Math.random() * 30 - 10, r: 5 + Math.random() * 7,
      c: colors[Math.floor(Math.random() * colors.length)], a: Math.random() * Math.PI, va: (Math.random() - 0.5) * 0.4,
    }));
    let raf, t0;
    const step = (t) => {
      if (!t0) t0 = t;
      const el = t - t0;
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        p.vy += 1.1; p.x += p.vx; p.y += p.vy; p.a += p.va; p.vx *= 0.99;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.a);
        ctx.globalAlpha = Math.max(0, 1 - el / 1800);
        ctx.fillStyle = p.c; ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
        ctx.restore();
      }
      if (el < 1800) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }} />;
}

// Renders a 1080×1350 share image of the workout.
async function renderShareImage({ session, sum, units, name }) {
  const c = document.createElement('canvas');
  c.width = 1080; c.height = 1350;
  const g = c.getContext('2d');
  const css = getComputedStyle(document.documentElement);
  const accent = css.getPropertyValue('--ll-accent').trim() || '#a3e635';
  try { await document.fonts?.ready; } catch {}
  g.fillStyle = '#0d0f12'; g.fillRect(0, 0, 1080, 1350);
  const grad = g.createLinearGradient(0, 0, 0, 600);
  grad.addColorStop(0, 'rgba(163,230,53,0.18)'); grad.addColorStop(1, 'rgba(163,230,53,0)');
  g.fillStyle = grad; g.fillRect(0, 0, 1080, 600);
  g.fillStyle = accent; g.font = '800 34px "Archivo Narrow", Arial'; g.fillText('LIFT LOG', 80, 110);
  g.fillStyle = '#f5f6f7'; g.font = '800 96px "Archivo Narrow", Arial'; g.fillText(session.name || 'Workout', 80, 230);
  g.fillStyle = '#9aa0a8'; g.font = '500 36px Inter, Arial';
  g.fillText(`${new Date(session.startedAt).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}${name ? ' · ' + name : ''}`, 80, 290);
  const stats = [['TIME', fmtDur(sum.duration)], ['VOLUME', `${volStr(sum.volume, units)} ${unitLabel(units)}`], ['SETS', String(sum.sets)], ['PRS', String(sum.prs.length)]];
  stats.forEach(([k, v], i) => {
    const x = 80 + (i % 2) * 470, y = 380 + Math.floor(i / 2) * 190;
    g.fillStyle = '#1a1d22'; roundRect(g, x, y, 450, 160, 28); g.fill();
    g.fillStyle = '#7d838c'; g.font = '700 28px Inter, Arial'; g.fillText(k, x + 36, y + 56);
    g.fillStyle = k === 'PRS' && sum.prs.length ? '#f2c14e' : '#f5f6f7'; g.font = '700 64px "Archivo Narrow", Arial'; g.fillText(v, x + 36, y + 128);
  });
  let y = 820;
  g.fillStyle = '#7d838c'; g.font = '700 28px Inter, Arial'; g.fillText(sum.prs.length ? 'PERSONAL RECORDS' : 'TOP SETS', 80, y); y += 30;
  const rows = sum.prs.length
    ? sum.prs.slice(0, 5).map(p => [p.name, p.metric === 'e1rm' ? setText({ ...p.now.st, rpe: null }, sum.exercises.find(e => e.slot.exerciseId === p.exerciseId)?.kind, units, { compact: true }) : prLine(p, units).to])
    : sum.exercises.filter(e => e.best).slice(0, 5).map(e => [e.name, setText({ ...e.best, rpe: null }, e.kind, units, { compact: true })]);
  rows.forEach(([n, v]) => {
    y += 70;
    g.fillStyle = '#f5f6f7'; g.font = '600 40px Inter, Arial'; g.fillText(n.length > 28 ? n.slice(0, 27) + '…' : n, 80, y);
    g.fillStyle = sum.prs.length ? '#f2c14e' : accent; g.font = '700 44px "Archivo Narrow", Arial';
    const w = g.measureText(v).width; g.fillText(v, 1000 - w, y);
  });
  return new Promise(res => c.toBlob(res, 'image/png'));
}
function roundRect(g, x, y, w, h, r) {
  g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}

export function WorkoutSummary({ sessionId, review = false }) {
  const { state, actions, replaceRoute, setRoute, goBack, toast, route } = useApp();
  useAIStatus(); // re-render when the coach connects / disconnects
  // Opened from a history search hit: scroll that exercise to the top and pulse it once.
  const focusEx = route?.params?.focusExerciseId || null;
  const focusRef = React.useRef(null);
  React.useEffect(() => {
    if (!focusEx) return undefined;
    const t = setTimeout(() => { try { focusRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch { /* old engines */ } }, 120);
    return () => clearTimeout(t);
  }, [focusEx]);
  const session = state.sessions.find(s => s.id === sessionId);
  const units = state.prefs.units;
  const [edit, setEdit] = React.useState(null);        // set being edited (review mode)
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const sum = React.useMemo(() => (session ? summarize(state, session) : null), [session, state.sessions]); // eslint-disable-line react-hooks/exhaustive-deps
  const [notes, setNotes] = React.useState(session?.notes || '');
  const [ai, setAi] = React.useState({ busy: false, err: null });
  const [details, setDetails] = React.useState(false);   // review: edit date / time / duration
  const [addEx, setAddEx] = React.useState(false);       // review: add an exercise
  const effort = state.prefs.effortScale === 'rir' ? 'rir' : 'rpe';
  const tSecs = useCountUp(sum?.duration || 0);
  const tVol = useCountUp(sum?.volume || 0);
  const tSets = useCountUp(sum?.sets || 0, 500);

  if (!session || !sum) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: LL.bg, gap: 12 }}>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, color: LL.fg }}>Workout not found</div>
        <button onClick={() => replaceRoute('home')} style={btn('ghost')}>Back home</button>
      </div>
    );
  }

  const debrief = session.aiDebrief || null;
  const genDebrief = async () => {
    setAi({ busy: true, err: null });
    try {
      const program = state.programs.find(p => p.id === session.programId);
      const recap = sum.exercises.map(e => `${e.name}: ${e.sets.map(s => `${TYPE_PREFIX[s.type] || ''}${setText(s, e.kind, 'lb')}`).join(', ')}${e.delta != null ? ` (best e1RM ${e.delta >= 0 ? '+' : ''}${e.delta.toFixed(1)}% vs last time)` : ''}`).join('\n')
        + (Object.keys(session.feedback || {}).length ? `\nMuscle check-ins: ${Object.entries(session.feedback).map(([m, f]) => `${m} pump ${f.pump || '?'}, soreness ${f.soreness || '?'}, joints ${f.joint || '?'}`).join('; ')}` : '');
      const text = await completeText({
        system: 'You are a concise, evidence-based strength coach inside a workout app. Write a post-workout debrief of 3–5 short sentences in plain text (no headings, no lists). Ground every statement in the data provided; never invent numbers. Mention what went well, one specific thing to watch or change next time, and the next-session target for the most important lift. Weights are in lb.',
        prompt: `Training context:\n${coachContext(state, program)}\n\nToday's session "${session.name || 'Workout'}" (${fmtDur(sum.duration)}, ${Math.round(sum.volume)} lb volume, ${sum.sets} working sets, ${sum.prs.length} PRs${session.feel ? `, felt ${session.feel}/5` : ''}${notes ? `, notes: "${notes}"` : ''}):\n${recap}`,
        maxTokens: 1500,
      });
      actions.updateSession(session.id, { aiDebrief: text });
      setAi({ busy: false, err: null });
    } catch (e) {
      setAi({ busy: false, err: e.message });
    }
  };

  const share = async () => {
    try {
      const blob = await renderShareImage({ session, sum, units, name: state.user.name });
      const file = new File([blob], 'workout.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: session.name || 'Workout' });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = `liftlog-${localDateStr(session.startedAt)}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        toast('Image saved');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast('Could not create the image');
    }
  };

  const done = () => { actions.updateSession(session.id, { notes }); if (review) goBack(); else replaceRoute('home'); };

  const saveEdit = (patch, remove) => {
    if (remove && session.sets.length <= 1) { setEdit(null); setConfirmDelete(true); return; }
    const before = session.sets;
    const exists = before.some(x => x.peId === edit.peId && x.setIdx === edit.setIdx);
    const sets = remove
      ? before.filter(x => !(x.peId === edit.peId && x.setIdx === edit.setIdx))
      : exists
        ? before.map(x => (x.peId === edit.peId && x.setIdx === edit.setIdx) ? { ...x, ...patch } : x)
        : [...before, { ...(({ isNew: _n, ...b }) => b)(edit), ...patch, ts: Math.min(session.completedAt || Date.now(), Math.max(...before.map(x => x.ts || 0), session.startedAt) + 60000) }];
    actions.updateCompletedSession(session.id, sets);
    setEdit(null);
    if (remove) toast('Set deleted', 'default', { action: { label: 'Undo', run: () => actions.updateCompletedSession(session.id, before) } });
  };
  // Review: add a set to an exercise (prefilled from its last set).
  const addSetTo = (e) => {
    const last = e.sets[e.sets.length - 1] || {};
    const setIdx = Math.max(-1, ...session.sets.filter(x => x.peId === e.slot.id).map(x => x.setIdx)) + 1;
    const rows = [...(e.slot.rows || [])];
    while (rows.length <= setIdx) rows.push({ type: 'normal' });
    const slots = sessionSlots(session, state.programs).map(sl => sl.id === e.slot.id ? { ...sl, rows } : sl);
    actions.updateSession(session.id, { exercises: slots });
    setEdit({ exerciseId: e.slot.exerciseId, peId: e.slot.id, setIdx, type: 'normal', w: last.w ?? 0, reps: last.reps ?? 8, rpe: null, ...(last.bw ? { bw: last.bw } : {}), ...(e.kind?.track === 'distance-time' ? { dist: last.dist || 0, dur: last.dur || 0, reps: 0, w: 0 } : {}), isNew: true });
  };
  // Review: add an exercise the workout forgot, then its first set.
  const addExercise = (ex) => {
    if (!ex) return;
    actions.ensureExercises([ex]);
    const sl = { ...slotFromExercise(ex, { targetWeight: 0 }), rows: [{ type: 'normal' }] };
    const slots = [...sessionSlots(session, state.programs), sl];
    actions.updateSession(session.id, { exercises: slots });
    const k = kindOf({ ...state, exercises: [...state.exercises, ex] }, ex.id);
    setEdit({ exerciseId: ex.id, peId: sl.id, setIdx: 0, type: 'normal', w: 0, reps: k.track === 'time' ? 30 : 8, rpe: null, ...(k.track === 'distance-time' ? { dist: 0, dur: 0, reps: 0 } : {}), isNew: true });
  };

  const repeat = () => {
    const live = state.sessions.find(x => x.id === state.activeSessionId && !x.completedAt);
    if (live) { toast('Finish your current workout first'); return; }
    const id = actions.startFreestyle(session.name || 'Workout');
    const slots = sum.exercises.map(e => ({ ...e.slot, id: slotId(), fromProgram: false, rows: e.sets.map(x => ({ type: x.type || 'normal', ...(x.tech ? { tech: x.tech } : {}) })) }));
    actions.updateSessionSlots(id, () => slots);
    setRoute('active-workout', { freestyle: session.name || 'Workout' });
  };
  const hasMuscles = Object.keys(sum.muscleValues).length > 0;
  const warmCount = session.sets.filter(x => x.type === 'warmup').length;

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: LL.bg, position: 'relative' }}>
      {sum.prs.length > 0 && !review && <Confetti />}
      {focusEx && <style>{`@keyframes llFocusPulse { 0%, 100% { background: transparent; } 30% { background: color-mix(in oklab, ${LL.accent} 16%, transparent); } }
.ll-focus-pulse { animation: llFocusPulse 1.6s ${LL.ease} 450ms both; }
@media (prefers-reduced-motion: reduce) { .ll-focus-pulse { animation: none; background: color-mix(in oklab, ${LL.accent} 10%, transparent); } }`}</style>}
      <div style={{ padding: '0 16px 18px', paddingTop: LL.pageTop, position: 'relative',
        background: `linear-gradient(180deg, color-mix(in oklab, ${sum.prs.length ? LL.pr : LL.accent} 9%, ${LL.bg}) 0%, ${LL.bg} 100%)` }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {review && <BackButton onClick={done} style={{ marginRight: 10 }} />}
          <div style={{ ...kicker, color: LL.accentText }}>{review ? 'Workout' : 'Workout complete'}</div>
          <BackButton label="Close" onClick={done} icon={<LLIcon.x />} style={{ marginLeft: 'auto' }} />
        </div>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.big, fontWeight: 800, color: LL.fg, letterSpacing: -1, lineHeight: 1, marginTop: 6 }}>
          {review ? (session.name || 'Workout') : sum.prs.length ? `${sum.prs.length} new PR${sum.prs.length > 1 ? 's' : ''}!` : sum.firsts ? `${plural(sum.firsts, 'first lift')} logged` : 'Nice work.'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 6 }}>
          <span>{review ? '' : `${session.name || 'Workout'} · `}{new Date(session.startedAt).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}{review ? ` · ${new Date(session.startedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}</span>
          {review && <button onClick={() => setDetails(true)} style={{ height: 32, padding: '0 12px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, cursor: 'pointer' }}>Edit details</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
          <Tile label="Time" value={fmtDur(tSecs)} />
          <Tile label="Volume" value={volStr(tVol, units)} unit={unitLabel(units)}
            sub={sum.easyDay ? (session.lighter ? 'Lighter day' : 'Deload week') : sum.lastVol ? `${sum.volume >= sum.lastVol ? '+' : ''}${Math.round((sum.volume / sum.lastVol - 1) * 100)}% vs last` : null}
            up={sum.easyDay || !sum.lastVol || Math.abs(sum.volume / sum.lastVol - 1) < 0.03 ? null : sum.volume >= sum.lastVol} />
          <Tile label="Working sets" value={fmtHalf(Math.round(tSets * 2) / 2)} sub={warmCount ? `+ ${plural(warmCount, 'warm-up')}` : null} />
          <Tile label="PRs" value={sum.prs.length} gold={sum.prs.length > 0} sub={sum.firsts ? `+ ${plural(sum.firsts, 'baseline')}` : null} up={true} />
        </div>
      </div>

      <div style={{ padding: '0 16px 30px' }}>
        {/* PRs */}
        {sum.prs.length > 0 && (
          <Section title="Personal records">
            {sum.prs.map((p, i) => (
              <div key={p.exerciseId + p.metric} className="ll-anim" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: i ? `1px solid ${LL.line}` : 'none', animation: reduce ? undefined : `llPrIn .45s ${LL.spring} ${i * 70}ms both` }}>
                <span style={{ width: 38, height: 38, borderRadius: LL.r.md, flexShrink: 0, background: `color-mix(in oklab, ${LL.pr} 20%, transparent)`, color: LL.prText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trophy /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>{p.name}</div>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {p.metric === 'e1rm' ? (() => {
                      const k = sum.exercises.find(e => e.slot.exerciseId === p.exerciseId)?.kind;
                      return <>{setText({ ...p.before.st, rpe: null }, k, units, { compact: true })} → <b style={{ color: LL.fg }}>{setText({ ...p.now.st, rpe: null }, k, units, { compact: true })}</b> · {simpleDefault(state) ? 'est. max' : 'e1RM'} {fmtWeight(Math.round(p.now.e1rm), units)} {unitLabel(units)}</>;
                    })() : (() => { const l = prLine(p, units); return <>{l.from} → <b style={{ color: LL.fg }}>{l.to}</b> · {l.tail}</>; })()}
                  </div>
                </span>
                <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.prText }}>+{p.gainPct.toFixed(1)}%</span>
                {aiEnabled() && (
                  <button aria-label={`Ask the coach about this ${p.name} PR`} onClick={() => openCoachWith({ kind: 'pr', exerciseId: p.exerciseId, label: p.name, ...(p.metric === 'e1rm' ? { w: p.now?.st?.w, reps: p.now?.st?.reps } : {}), date: session.startedAt }, setRoute)}
                    style={{ width: 44, height: 44, marginRight: -10, flexShrink: 0, borderRadius: LL.r.pill, border: 'none', background: 'transparent', color: LL.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                    <LLIcon.ai style={{ width: 18, height: 18 }} />
                  </button>
                )}
              </div>
            ))}
          </Section>
        )}

        {!review && <AchievementsUnlocked session={session} style={{ marginTop: 16 }} />}

        {/* Debrief */}
        <Section title="Coach's take" right={aiEnabled() && !debrief && !ai.busy ? <button onClick={genDebrief} style={{ minHeight: 44, margin: '-14px -4px -14px 0', padding: '0 4px', background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer', display: 'flex', gap: 4, alignItems: 'center' }}><LLIcon.ai style={{ width: 14, height: 14 }} /> AI debrief</button> : null}>
          <div style={{ padding: 14, fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg, lineHeight: 1.5 }}>
            {ai.busy ? <span style={{ color: LL.fgMute }}>Analyzing your session…</span> : debrief || localDebrief(sum, units, session, simpleDefault(state))}
            {ai.err && <div style={{ marginTop: 8, color: LL.danger, fontSize: LL.fs.meta }}>{ai.err}</div>}
          </div>
          {aiEnabled() && (
            <button onClick={() => openCoachWith({ kind: 'session', sessionId: session.id }, setRoute)} style={{ width: '100%', minHeight: 48, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', borderTop: `1px solid ${LL.line}`, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
              <LLIcon.ai style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Ask about this workout</span>
              <LLIcon.chev style={{ color: LL.fgFaint, flexShrink: 0 }} />
            </button>
          )}
        </Section>

        {/* Muscles */}
        {hasMuscles && (
          <Section title="Muscles worked">
            <div style={{ padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}><MuscleMap values={sum.muscleValues} view="both" height={250} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 18, rowGap: 6, marginTop: 12 }}>
                {Object.entries(sum.muscles).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([m, n]) => (
                  <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m}</span>
                    <span style={{ color: LL.fg, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{plural(Math.max(1, Math.round(n)), 'set')}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 10 }}>Secondary muscles count as half a set.</div>
            </div>
          </Section>
        )}

        {/* Next time */}
        <NextTime session={session} units={units} simple={simpleDefault(state)} sum={sum} />

        {/* Recap */}
        <Section title="Exercises">
          {sum.exercises.map((e, i) => {
            const focused = focusEx && e.slot.exerciseId === focusEx;
            return (
            <div key={e.slot.id} ref={focused ? focusRef : undefined} className={focused ? 'll-focus-pulse' : undefined}
              style={{ padding: '6px 14px 12px', borderTop: i ? `1px solid ${LL.line}` : 'none', scrollMarginTop: 12 }}>
              {/* Only the name opens the exercise; the chips below are their own buttons. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setRoute('exercise', { exerciseId: e.slot.exerciseId })} style={{ flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'center', gap: 4, padding: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>
                  <span style={{ minWidth: 0 }}>{e.name}</span>
                  <LLIcon.chev style={{ width: 16, height: 16, color: LL.fgFaint, flexShrink: 0 }} />
                </button>
                {e.plan ? <PlanStatus plan={e.plan} />
                  : e.delta != null && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, color: e.delta >= 0.5 ? LL.accentText : e.delta > -3 ? LL.fgMute : LL.warn }}>{e.delta >= 0 ? '+' : ''}{e.delta.toFixed(1)}%</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                {e.sets.map((s, k) => (
                  <button key={k} aria-label={`Edit set ${k + 1} of ${e.name}`} onClick={() => setEdit({ ...s })} style={{ cursor: 'pointer', minHeight: 36, display: 'inline-flex', alignItems: 'center', boxSizing: 'border-box', border: 'none',
                    padding: '0 10px', borderRadius: LL.r.sm, fontFamily: LL.ui, fontSize: LL.fs.sm, fontVariantNumeric: 'tabular-nums',
                    background: s === e.best ? LL.accentSoft : LL.surface2, color: s.type === 'warmup' || s.type === 'mini' ? LL.fgMute : (s === e.best ? LL.accentText : LL.fg),
                    fontWeight: s === e.best ? 700 : 500,
                  }}>{TYPE_PREFIX[s.type] || ''}{setText(s, e.kind, units, { effort, compact: true })}</button>
                ))}
                {review && (
                  <button aria-label={`Add a set to ${e.name}`} onClick={() => addSetTo(e)} style={{ cursor: 'pointer', minHeight: 36, padding: '0 10px', borderRadius: LL.r.sm, border: `1px dashed ${LL.line2}`, background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><LLIcon.plus style={{ width: 13, height: 13 }} />Set</button>
                )}
              </div>
            </div>
            );
          })}
          {review && (
            <button onClick={() => setAddEx(true)} style={{ width: '100%', height: 48, border: 'none', borderTop: `1px solid ${LL.line}`, background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <LLIcon.plus style={{ width: 16, height: 16 }} /> Add exercise
            </button>
          )}
        </Section>

        {/* Muscle check-in: answers, and any muscle still open (never required) */}
        <CheckInSection state={state} session={session} sum={sum} actions={actions} />

        {/* Feel + notes */}
        <Section title="How did it feel?">
          <div style={{ padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
              {['Rough', 'Meh', 'Solid', 'Strong', 'Great'].map((l, i) => {
                const on = session.feel === i + 1;
                return (
                  <button key={l} onClick={() => actions.updateSession(session.id, { feel: on ? null : i + 1 })} style={{
                    height: 46, borderRadius: LL.r.sm, cursor: 'pointer', fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
                    border: `1px solid ${on ? LL.accent : LL.line}`, background: on ? LL.accentSoft : LL.surface2, color: on ? LL.accentText : LL.fgMute,
                  }}>{l}</button>
                );
              })}
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => actions.updateSession(session.id, { notes })} rows={3}
              placeholder="Notes — sleep, energy, anything that hurt…"
              style={{ marginTop: 10, width: '100%', padding: 12, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, outline: 'none', resize: 'vertical' }} />
          </div>
        </Section>

        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 12, lineHeight: 1.45 }}>
          Typo? Tap any set to correct it. Records update automatically{review ? '; next-session targets were set when you finished' : ''}.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={share} style={{ ...btn('ghost'), flex: 1 }}><LLIcon.upRight /> Share</button>
          {review && <button onClick={repeat} style={{ ...btn('ghost'), flex: 1 }}><LLIcon.swap /> Repeat</button>}
          <button data-ll-primary onClick={done} style={{ ...btn('accent'), flex: review ? 1 : 2 }}>Done</button>
        </div>
        {review && (
          <button onClick={() => setConfirmDelete(true)} style={{ ...btn('ghost'), width: '100%', marginTop: 8, color: LL.danger }}>Delete workout</button>
        )}
        <LLSheet open={!!edit} onClose={() => setEdit(null)} title={edit?.isNew ? 'Add set' : 'Edit set'} subtitle={edit ? exerciseNameFor(state, edit.exerciseId) : ''}>
          {edit && (() => {
            const k = kindOf(state, edit.exerciseId);
            const cardio = k.track === 'distance-time' || edit.dist > 0 || edit.dur > 0;
            return (
            <div>
              {cardio ? (
                <CardioEdit key={`${edit.peId}-${edit.setIdx}`} dist={edit.dist} dur={edit.dur} units={units} onChange={(p) => setEdit(x => ({ ...x, ...p }))} />
              ) : (
                <>
                  {isBwLoad(k.load) && <div style={{ ...kicker, margin: '0 0 6px' }}>{k.load === 'assisted' ? 'Assistance' : 'Added weight'} · 0 = body weight only</div>}
                  <WeightRepInput key={`${edit.peId}-${edit.setIdx}`} w={edit.w} reps={edit.reps} pe={{ group: 'Other' }} units={units} haptics={false}
                    onChange={(p) => setEdit(x => ({ ...x, ...p }))} />
                </>
              )}
              <div style={{ ...kicker, margin: '2px 0 8px' }}>{effort === 'rir' ? 'Reps in reserve' : 'Effort (RPE)'} · optional</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
                {[6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, null].map(v => {
                  const on = (edit.rpe ?? null) === v;
                  const label = v == null ? 'None' : effort === 'rir' ? (v <= 6 ? '4+' : String(rirFromRpe(v))) : v;
                  return (
                    <button key={String(v)} onClick={() => setEdit(x => ({ ...x, rpe: v }))} aria-pressed={on} aria-label={v == null ? 'No effort rating' : effort === 'rir' ? `${label} reps in reserve` : `RPE ${v}`} style={{ height: 44, borderRadius: LL.r.sm, cursor: 'pointer', border: `1px solid ${on ? LL.accent : LL.line}`, background: on ? LL.accentSoft : LL.surface2, color: on ? LL.accentText : v == null ? LL.fgMute : LL.fg, fontFamily: v == null ? LL.ui : LL.display, fontSize: v == null ? LL.fs.meta : LL.fs.lg, fontWeight: v == null ? 600 : 700, whiteSpace: 'nowrap' }}>{label}</button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!edit.isNew && <button onClick={() => saveEdit(null, true)} style={{ ...btn('ghost'), color: LL.danger }}>Delete</button>}
                <button disabled={cardio ? !(edit.dist > 0 || edit.dur > 0) : !(edit.reps > 0)} onClick={() => {
                  const { isNew: _n, ...clean } = edit;
                  saveEdit(cardio ? { ...clean, w: 0, reps: 0, dist: edit.dist || 0, dur: edit.dur || 0, rpe: edit.rpe ?? null } : { ...clean, w: edit.w, reps: edit.reps, rpe: edit.rpe ?? null });
                }} style={{ ...btn('accent'), flex: 1, opacity: (cardio ? (edit.dist > 0 || edit.dur > 0) : edit.reps > 0) ? 1 : 0.5 }}>{edit.isNew ? 'Add set' : 'Save'}</button>
              </div>
            </div>
            );
          })()}
        </LLSheet>
        <DetailsSheet open={details} session={session} sum={sum} onClose={() => setDetails(false)} onSave={(patch) => { actions.updateSession(session.id, patch); setDetails(false); toast('Workout details saved'); }} />
        <ExercisePicker open={addEx} title="Add exercise" onClose={() => setAddEx(false)} onPick={(exs) => { setAddEx(false); if (exs && exs[0]) addExercise(exs[0]); }} />
        <LLSheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this workout?" subtitle="This can not be undone">
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setConfirmDelete(false)} style={{ ...btn('ghost'), flex: 1 }}>Keep it</button>
            <button onClick={() => { setConfirmDelete(false); actions.deleteSession(session.id); replaceRoute('home'); }} style={{ ...btn('accent'), flex: 1, background: LL.danger, color: '#fff' }}>Delete</button>
          </div>
        </LLSheet>
      </div>
    </div>
  );
}

function btn(kind) {
  return {
    height: 52, borderRadius: LL.r.md, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, padding: '0 16px',
    ...(kind === 'accent' ? { background: LL.accent, color: LL.accentInk, border: 'none' } : { background: LL.surface, color: LL.fg, border: `1px solid ${LL.line2}` }),
  };
}

function Section({ title, right, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, padding: '0 2px' }}>
        <div style={kicker}>{title}</div>
        {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
      </div>
      <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, overflow: 'hidden' }}>{children}</div>
    </div>
  );
}

function Tile({ label, value, unit, sub, up, gold }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${gold ? `color-mix(in oklab, ${LL.pr} 45%, ${LL.line})` : LL.line}` }}>
      <div style={kicker}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
        <span style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 700, color: gold ? LL.prText : LL.fg, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, marginTop: 4, color: up === true ? LL.accentText : up === false ? LL.warn : LL.fgDim }}>{sub}</div>}
    </div>
  );
}

// "Next time" as a scannable table: exercise left, next load + delta right. The
// engine's reason ("top of the range on every set") is said once, not per row.
function nextRows(session, units) {
  const changes = Array.isArray(session.progressionChanges) ? session.progressionChanges : [];
  const prog = Array.isArray(session.progressedExercises) ? session.progressedExercises : [];
  const byName = Object.fromEntries(prog.map(p => [p.name, p]));
  const rows = [];
  const seen = new Set();
  for (const t of changes) {
    const m = /^(.+?): (.*)$/.exec(t || '');
    if (!m) { rows.push({ name: t, note: '' }); continue; }
    const [, name, body] = m;
    const reason = body.split(/ — /)[0].replace(/\.$/, '');
    const p = byName[name];
    const tm = /training max ([\d.]+) → ([\d.]+) (lb|kg)/.exec(body);
    if (p && !seen.has(name)) {
      seen.add(name);
      rows.push({ name, value: `${fmtWeight(p.newWeight, units)} ${unitLabel(units)}`, delta: p.increment > 0 ? `+${fmtWeight(p.increment, units)}` : null, reason });
    } else if (tm) {
      const d = Math.round((+tm[2] - +tm[1]) * 10) / 10;
      rows.push({ name, value: `TM ${tm[2]} ${tm[3]}`, delta: d ? `${d > 0 ? '+' : ''}${d}` : null, down: d < 0, note: (/\((.*)\)/.exec(body) || [])[1] || '' });
    } else {
      rows.push({ name, note: body.replace(/\.$/, '') });
    }
  }
  for (const p of prog) if (!seen.has(p.name) && !changes.length) rows.push({ name: p.name, value: `${fmtWeight(p.newWeight, units)} ${unitLabel(units)}`, delta: p.increment > 0 ? `+${fmtWeight(p.increment, units)}` : null });
  const reasons = [...new Set(rows.map(r => r.reason).filter(Boolean))];
  return { rows, caption: reasons.length === 1 ? reasons[0][0].toUpperCase() + reasons[0].slice(1) : null };
}

function NextTime({ session, units, simple = false, sum = null }) {
  const [all, setAll] = React.useState(false);
  let { rows, caption } = nextRows(session, units);
  if (simple) {
    // Simple mode: every row is a next step in plain words ("Try 140 lb").
    rows = rows.map(r => r.value ? { ...r, value: /^TM /.test(r.value) ? r.value.replace(/^TM /, 'Working max ') : `Try ${r.value}` } : r);
    const named = new Set(rows.map(r => r.name));
    for (const e of (sum?.exercises || [])) {
      if (named.has(e.name) || e.kind?.track !== 'reps' || !e.best || !(e.slot.repMax > 0)) continue;
      const work = e.sets.filter(x => x.type !== 'warmup' && x.type !== 'mini' && x.reps > 0);
      const top = Math.max(0, ...work.map(x => x.w || 0));
      const atTop = work.filter(x => (x.w || 0) === top);
      if (!top || !atTop.length || atTop.every(x => x.reps >= e.slot.repMax)) continue;
      const low = Math.min(...atTop.map(x => x.reps));
      rows.push({ name: e.name, value: `${fmtWeight(top, units)} ${unitLabel(units)}`, note: `Same weight — aim for ${Math.min(low + 1, e.slot.repMax)} reps on every set` });
    }
  }
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 6);
  return (
    <Section title="Next time">
      {caption && <div style={{ padding: '10px 14px 0', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>{caption}, so the weight goes up.</div>}
      {simple && !caption && <div style={{ padding: '10px 14px 0', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>Your next step on each lift.</div>}
      {shown.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: i || caption || simple ? `1px solid ${LL.line}` : 'none', marginTop: i === 0 && (caption || simple) ? 10 : 0 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{r.name}</div>
            {(r.note || (!caption && r.reason)) && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2, lineHeight: 1.35 }}>{r.note || (r.reason[0].toUpperCase() + r.reason.slice(1))}</div>}
          </span>
          {r.value && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.value}</span>}
          {r.delta && <span style={{ minWidth: 38, textAlign: 'right', fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: r.down ? LL.warn : LL.accentText, fontVariantNumeric: 'tabular-nums' }}>{r.delta}</span>}
        </div>
      ))}
      {rows.length > 6 && (
        <button onClick={() => setAll(v => !v)} style={{ width: '100%', height: 44, border: 'none', borderTop: `1px solid ${LL.line}`, background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' }}>
          {all ? 'Show less' : `+${rows.length - 6} more`}
        </button>
      )}
    </Section>
  );
}

// "3/3 as planned", "+4 on the + set", "1 set short": judged against the prescription.
function PlanStatus({ plan }) {
  const base = { fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  if (plan.plus && plan.plus.reps > plan.plus.min) return <span style={{ ...base, color: LL.accentText }}>+{plan.plus.reps - plan.plus.min} on the + set</span>;
  if (plan.short > 0) return <span style={{ ...base, color: LL.fgMute }}>{plural(plan.short, 'set')} short</span>;
  if (plan.hit > 0) return <span style={{ ...base, color: LL.accentText }}>{plan.hit}/{plan.total} as planned</span>;
  return null;
}

const fmtHalf = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

// Distance + time editor for cardio sets (review / summary).
function CardioEdit({ dist, dur, units, onChange }) {
  const [d, setD] = React.useState(dist > 0 ? String(+metersTo(dist, units).toFixed(2)) : '');
  const [t, setT] = React.useState(dur > 0 ? fmtDuration(dur) : '');
  const field = { width: '100%', boxSizing: 'border-box', height: 52, padding: '0 14px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, outline: 'none' };
  const pace = paceSecPerKm(metersFrom(parseFloat(d), units), parseDuration(t));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
      <label style={{ display: 'block' }}>
        <div style={{ ...kicker, marginBottom: 6 }}>Distance · {distUnit(units)}</div>
        <input value={d} inputMode="decimal" placeholder="0.00" aria-label={`Distance in ${distUnit(units)}`} onChange={e => { const v = e.target.value.replace(/[^\d.,]/g, ''); setD(v); onChange({ dist: Math.round(metersFrom(parseFloat(v.replace(',', '.')), units)) }); }} style={field} />
      </label>
      <label style={{ display: 'block' }}>
        <div style={{ ...kicker, marginBottom: 6 }}>Time · mm:ss</div>
        <input value={t} inputMode="numeric" placeholder="25:30" aria-label="Time" onChange={e => { const v = e.target.value.replace(/[^\d:]/g, ''); setT(v); onChange({ dur: parseDuration(v) || 0 }); }} style={field} />
      </label>
      {pace && <div style={{ gridColumn: '1 / span 2', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>Pace {fmtPace(pace, units)}</div>}
    </div>
  );
}

// Review: date, start time and duration of a past workout.
function DetailsSheet({ open, session, onClose, onSave }) {
  const toDate = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const toTime = (ts) => { const d = new Date(ts); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
  const [name, setName] = React.useState('');
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [mins, setMins] = React.useState('');
  React.useEffect(() => {
    if (!open || !session) return;
    setName(session.name || ''); setDate(toDate(session.startedAt)); setTime(toTime(session.startedAt));
    setMins(String(Math.max(1, Math.round(((session.completedAt || session.startedAt) - session.startedAt) / 60000))));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!session) return <LLSheet open={false} onClose={onClose} />;
  const start = Date.parse(`${date}T${time || '18:00'}:00`);
  const m = parseInt(mins, 10);
  const ok = Number.isFinite(start) && start <= Date.now() && m > 0 && m <= 600;
  const input = { width: '100%', boxSizing: 'border-box', height: 48, padding: '0 12px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, outline: 'none', colorScheme: 'inherit' };
  const lab = (t, child) => <label style={{ display: 'block' }}><div style={{ ...kicker, marginBottom: 6 }}>{t}</div>{child}</label>;
  return (
    <LLSheet open={open} onClose={onClose} title="Workout details" subtitle="Name, date, start time and duration">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {lab('Name', <input value={name} onChange={e => setName(e.target.value)} maxLength={60} style={input} />)}
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 8 }}>
          {lab('Date', <input type="date" value={date} max={toDate(Date.now())} onChange={e => setDate(e.target.value)} style={input} />)}
          {lab('Start', <input type="time" value={time} onChange={e => setTime(e.target.value)} style={input} />)}
        </div>
        {lab('Duration · minutes', <input value={mins} inputMode="numeric" onChange={e => setMins(e.target.value.replace(/\D/g, '').slice(0, 3))} style={input} />)}
        {!ok && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.danger }}>{start > Date.now() ? 'The start can not be in the future.' : 'Check the date, time and duration.'}</div>}
        <button disabled={!ok} onClick={() => {
          const shift = start - session.startedAt;
          onSave({ name: name.trim() || session.name, startedAt: start, completedAt: start + m * 60000, sets: (session.sets || []).map(st => ({ ...st, ts: st.ts ? Math.min(start + m * 60000, Math.max(start, st.ts + shift)) : st.ts })) });
        }} style={{ ...btn('accent'), opacity: ok ? 1 : 0.5 }}>Save details</button>
      </div>
    </LLSheet>
  );
}

// Per-muscle check-in on the summary: the answers given, any muscle still
// open (one tap each), and what they mean for next week.
const SIGNAL_TEXT = { add: 'room for more sets', hold: 'hold the volume', reduce: 'pull back a little' };
function CheckInSection({ state, session, sum, actions }) {
  if ((state.prefs.muscleFeedback === false || simpleDefault(state)) && !Object.keys(session.feedback || {}).length) return null;
  const trained = Object.entries(sum.muscles).filter(([, n]) => n >= 1).sort((a, b) => b[1] - a[1]).map(([m]) => m)
    .filter(m => {
      // primary muscles of today's lifts only
      return sum.exercises.some(e => (musclesFor(state.exercises.find(x => x.id === e.slot.exerciseId) || { group: e.slot.group }).primary || []).includes(m) && e.kind?.track !== 'distance-time');
    });
  const fb = session.feedback || {};
  const skipped = new Set(session.feedbackSkipped || []);
  const list = trained.filter(m => fb[m] || !skipped.has(m));
  if (!list.length) return null;
  const answer = (m, patch) => {
    const cur = fb[m] || { pump: null, soreness: null, joint: null };
    const next = { ...cur, ...patch };
    if (next.joint !== 'bad' && next.joint !== 'some') delete next.jointEx;
    actions.updateSession(session.id, { feedback: { ...fb, [m]: next } });
  };
  const exFor = (m) => sum.exercises.filter(e => (musclesFor(state.exercises.find(x => x.id === e.slot.exerciseId) || { group: e.slot.group }).primary || []).includes(m)).map(e => ({ id: e.slot.exerciseId, name: e.name }));
  const prior = state.sessions.filter(s => s.completedAt && s.id !== session.id && s.startedAt < session.startedAt && s.startedAt > session.startedAt - 21 * 86400000);
  const soreAsk = (m) => prior.some(s => (s.sets || []).some(st => st.type !== 'warmup' && (musclesFor(state.exercises.find(x => x.id === st.exerciseId)).primary || []).includes(m)));
  const isDone = (m) => { const v = fb[m]; return !!v && v.pump != null && v.joint != null && (v.soreness != null || !soreAsk(m)); };
  const done = list.filter(isDone);
  const open = list.filter(m => !isDone(m));
  const withThis = { ...state, sessions: state.sessions.map(s => s.id === session.id ? { ...s, completedAt: s.completedAt || Date.now() } : s) };
  const skip = (m) => actions.updateSession(session.id, { feedbackSkipped: [...new Set([...(session.feedbackSkipped || []), m])] });
  return (
    <Section title="Muscle check-in">
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {done.map(m => {
          const t = muscleFeedbackTrend(withThis, m);
          return (
            <div key={m}>
              <MuscleCheck muscle={m} value={fb[m]} askSoreness={soreAsk(m)} exercises={exFor(m)} done
                onAnswer={(patch) => answer(m, patch)} onSkip={() => skip(m)} onOff={() => actions.setPref('muscleFeedback', false)} />
              {t.signal && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, margin: '6px 4px 0' }}>Next week: <b style={{ color: LL.fg, fontWeight: 600 }}>{SIGNAL_TEXT[t.signal]}</b> · {t.reason.toLowerCase()}</div>}
            </div>
          );
        })}
        {open.length > 0 && (
          <FinishCheckIn bare muscles={open.map(m => ({ muscle: m }))} feedback={fb} soreAsk={{ has: soreAsk }} exercisesOf={(x) => exFor(x.muscle)}
            onAnswer={answer} onSkip={skip} onOff={() => actions.setPref('muscleFeedback', false)} />
        )}
      </div>
    </Section>
  );
}

function Trophy() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M6 3h8v4a4 4 0 01-8 0V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6 5H3.5a2.5 2.5 0 002.7 3M14 5h2.5a2.5 2.5 0 01-2.7 3M10 11v3M7 17h6M8 14h4v3H8z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
